import React, { useEffect, useState } from "react";
import { api, fmtMoney, fmtDate, formatApiError, todayLocal, attachmentUrl } from "@/lib/api";
import { loadExpenseCategories, DEFAULT_EXPENSE_CATEGORIES } from "@/lib/categories";
import ListSearch, { filterBy } from "@/components/ListSearch";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Receipt, Pencil, Copy, ExternalLink, ScanLine, Sparkles, Loader2 } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import FileUpload from "@/components/FileUpload";
import CurrencyRateInput from "@/components/CurrencyRateInput";
import { toast } from "sonner";

const CATEGORIES = DEFAULT_EXPENSE_CATEGORIES;

export default function Expenses() {
    const { currentOrg } = useAuth();
    const [items, setItems] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [bankAccounts, setBankAccounts] = useState([]);
    const [categories, setCategories] = useState(CATEGORIES);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const today = todayLocal();
    const blank = { date: today, amount: 0, tax: 0, category: "Uncategorized", description: "", vendor_id: "", bank_account_id: "", payment_method: "card", attachment_id: "", currency: currentOrg?.currency || "KYD", fx_rate: 1 };
    const [form, setForm] = useState(blank);
    const [attachment, setAttachment] = useState(null);
    const [q, setQ] = useState("");
    const [scanOpen, setScanOpen] = useState(false);
    const [scanFile, setScanFile] = useState(null);
    const [scanning, setScanning] = useState(false);

    const load = async () => {
        const [{ data: ex }, { data: v }, { data: ba }, cats] = await Promise.all([
            api.get("/expenses"),
            api.get("/vendors"),
            api.get("/bank-accounts").catch(() => ({ data: { items: [] } })),
            loadExpenseCategories(),
        ]);
        setItems(ex.items || []);
        setVendors(v.items || []);
        setBankAccounts(ba.items || []);
        setCategories(cats);
    };
    useEffect(() => { if (currentOrg) load(); }, [currentOrg]);

    const submit = async () => {
        const payload = {
            ...form,
            vendor_id: form.vendor_id || null,
            bank_account_id: form.bank_account_id || null,
            amount: Number(form.amount),
            tax: Number(form.tax),
            currency: form.currency || (currentOrg?.currency || "KYD"),
            fx_rate: Number(form.fx_rate) || 1,
            attachment_id: attachment?.id || null,
        };
        try {
            if (editing) {
                await api.patch(`/expenses/${editing.id}`, payload);
                toast.success("Expense updated");
            } else {
                await api.post("/expenses", payload);
                toast.success("Expense logged");
            }
            setOpen(false); setEditing(null); setForm(blank); setAttachment(null); load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const openCreate = () => {
        setEditing(null);
        setForm(blank);
        setAttachment(null);
        setOpen(true);
    };

    const openEdit = (e) => {
        setEditing(e);
        setForm({
            date: e.date,
            amount: e.amount,
            tax: e.tax || 0,
            category: e.category || "Uncategorized",
            description: e.description || "",
            vendor_id: e.vendor_id || "",
            bank_account_id: e.bank_account_id || "",
            payment_method: e.payment_method || "card",
            attachment_id: e.attachment_id || "",
            currency: e.currency || (currentOrg?.currency || "KYD"),
            fx_rate: e.fx_rate || 1,
        });
        // If existing attachment_id, show as attached (filename unknown — placeholder).
        setAttachment(e.attachment_id ? { id: e.attachment_id, filename: "Attached receipt", size: 0, content_type: "application/pdf" } : null);
        setOpen(true);
    };

    const remove = async (id) => {
        if (!window.confirm("Delete?")) return;
        try { await api.delete(`/expenses/${id}`); load(); } catch (e) { toast.error(formatApiError(e)); }
    };

    const duplicate = async (id) => {
        try {
            await api.post(`/expenses/${id}/duplicate`);
            toast.success("Expense duplicated");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const runScan = async () => {
        if (!scanFile?.id) { toast.error("Upload a receipt first"); return; }
        setScanning(true);
        try {
            const { data } = await api.post("/expenses/scan", { attachment_id: scanFile.id });
            const d = data.draft || {};
            if (data.vendor_was_created && data.vendor) {
                setVendors((prev) => [...prev, data.vendor]);
                toast.success(`New vendor created: ${data.vendor.name}`);
            } else if (data.vendor) {
                toast.success(`Matched vendor: ${data.vendor.name}`);
            }
            setEditing(null);
            setForm({
                date: d.date || today,
                amount: d.amount || 0,
                tax: d.tax || 0,
                category: d.category || "Uncategorized",
                description: d.description || "",
                vendor_id: d.vendor_id || "",
                bank_account_id: "",
                payment_method: d.payment_method || "card",
                attachment_id: d.attachment_id || scanFile.id,
                currency: d.currency || (currentOrg?.currency || "KYD"),
                fx_rate: d.fx_rate ?? 1,
            });
            setAttachment(scanFile);
            setScanOpen(false);
            setScanFile(null);
            setOpen(true);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setScanning(false);
        }
    };

    const currency = currentOrg?.currency || "KYD";

    return (
        <div>
            <PageHeader
                subtitle="Purchases"
                title="Expenses"
                action={
                    <div className="flex items-center gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => { setScanFile(null); setScanOpen(true); }}
                            className="rounded-xl border-secondary/40 text-secondary-dark hover:bg-secondary/10"
                            data-testid="scan-expense-btn"
                        >
                            <ScanLine className="h-4 w-4 mr-1.5" /> Scan receipt
                        </Button>
                        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(blank); setAttachment(null); } }}>
                            <DialogTrigger asChild><Button onClick={openCreate} className="bg-ocean-700 text-white rounded-xl" data-testid="new-expense-btn"><Plus className="h-4 w-4 mr-1.5" /> Log expense</Button></DialogTrigger>
                        <DialogContent>
                            <DialogHeader><DialogTitle className="font-heading text-2xl">{editing ? "Edit expense" : "Log expense"}</DialogTitle></DialogHeader>
                            <div className="space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                    <div><Label>Date</Label><Input className="mt-1.5" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
                                    <div><Label>Amount</Label><Input className="mt-1.5" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="expense-amount-input" /></div>
                                </div>
                                <div>
                                    <Label>Category</Label>
                                    <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                                        <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                                        <SelectContent>{Array.from(new Set([...(form.category ? [form.category] : []), ...categories])).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label>Vendor (optional)</Label>
                                    <Select value={form.vendor_id} onValueChange={(v) => setForm({ ...form, vendor_id: v })}>
                                        <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select vendor" /></SelectTrigger>
                                        <SelectContent>{vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
                                    </Select>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label>Payment method</Label>
                                        <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                                            <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="cash">Cash</SelectItem>
                                                <SelectItem value="card">Card</SelectItem>
                                                <SelectItem value="bank">Bank transfer</SelectItem>
                                                <SelectItem value="cheque">Cheque</SelectItem>
                                                <SelectItem value="other">Other</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label>Paid from account</Label>
                                        <Select value={form.bank_account_id} onValueChange={(v) => setForm({ ...form, bank_account_id: v })}>
                                            <SelectTrigger className="mt-1.5" data-testid="expense-bank-account"><SelectValue placeholder={bankAccounts.length ? "Select account" : "No bank accounts"} /></SelectTrigger>
                                            <SelectContent>{bankAccounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                <div><Label>Description</Label><Input className="mt-1.5" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                                <CurrencyRateInput
                                    baseCurrency={currentOrg?.currency || "KYD"}
                                    currency={form.currency}
                                    fxRate={form.fx_rate}
                                    amount={Number(form.amount) || 0}
                                    onCurrency={(c) => setForm({ ...form, currency: c })}
                                    onFxRate={(r) => setForm({ ...form, fx_rate: r })}
                                    testId="expense-fx"
                                />
                                <FileUpload
                                    value={attachment}
                                    onChange={setAttachment}
                                    testId="expense-attachment"
                                    onScan={(parsed) => {
                                        setForm((prev) => ({
                                            ...prev,
                                            date: parsed.date || prev.date,
                                            amount: parsed.total != null && parsed.total > 0 ? String(parsed.total) : prev.amount,
                                            tax: parsed.tax != null && parsed.tax > 0 ? String(parsed.tax) : prev.tax,
                                            category: parsed.suggested_category && categories.includes(parsed.suggested_category) ? parsed.suggested_category : prev.category,
                                            description: parsed.merchant ? (prev.description ? prev.description : parsed.merchant) : prev.description,
                                            vendor_name: parsed.merchant || prev.vendor_name,
                                        }));
                                    }}
                                />
                            </div>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                                <Button onClick={submit} className="bg-ocean-700 text-white" data-testid="submit-expense-btn">{editing ? "Save changes" : "Save"}</Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                    </div>
                }
            />
            {items.length === 0 ? (
                <EmptyState icon={Receipt} title="No expenses yet" description="Log your first expense, or upload a receipt for auto-capture." />
            ) : (
                <>
                    <div className="mb-4 flex items-center gap-3">
                        <ListSearch value={q} onChange={setQ} placeholder="Search expenses by description, vendor or category…" testId="expenses-search" />
                        {q && <span className="text-xs text-muted-foreground">{filterBy(items, q, ["description", "vendor_name", "category"]).length} of {items.length}</span>}
                    </div>
                    <div className="rounded-3xl bg-white border border-sand-200 shadow-card overflow-hidden">
                        <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0 [scrollbar-width:thin] relative">
                            <table className="w-full text-sm min-w-[720px] sm:min-w-0">
                                <thead className="bg-sand-100 text-xs uppercase tracking-wider text-muted-foreground">
                                    <tr>
                                        <th className="text-left px-5 py-3">Date</th>
                                        <th className="text-left px-5 py-3">Description</th>
                                        <th className="text-left px-5 py-3">Vendor</th>
                                        <th className="text-left px-5 py-3">Category</th>
                                        <th className="text-center px-5 py-3">Receipt</th>
                                        <th className="text-right px-5 py-3">Amount</th>
                                        <th className="px-5 py-3"></th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-sand-200">
                                    {filterBy(items, q, ["description", "vendor_name", "category"]).map((r) => (
                                        <tr key={r.id} className="hover:bg-sand-50/50">
                                            <td className="px-5 py-3 text-muted-foreground">{fmtDate(r.date)}</td>
                                            <td className="px-5 py-3 text-ocean-700">{r.description || "—"}</td>
                                            <td className="px-5 py-3 text-muted-foreground">{r.vendor_name || "—"}</td>
                                            <td className="px-5 py-3"><Badge className="bg-sand-200 text-ocean-700 border-0">{r.category}</Badge></td>
                                            <td className="px-5 py-3 text-center">
                                                {r.attachment_id ? (
                                                    <a
                                                        href={attachmentUrl(r.attachment_id)}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="inline-flex items-center gap-1 text-secondary-dark hover:text-ocean-700 text-xs font-medium"
                                                        data-testid={`expense-receipt-${r.id}`}
                                                    >
                                                        <Receipt className="h-3.5 w-3.5" />
                                                        <ExternalLink className="h-3 w-3" />
                                                    </a>
                                                ) : (
                                                    <span className="text-xs text-muted-foreground">—</span>
                                                )}
                                            </td>
                                            <td className="px-5 py-3 font-mono text-right">
                                                <div className="inline-flex items-center gap-2 justify-end">
                                                    {r.currency && r.currency !== currency && (
                                                        <span className="text-[10px] font-medium tracking-wide text-secondary-dark bg-secondary/10 rounded-md px-1.5 py-0.5" data-testid={`expense-fx-chip-${r.id}`} title={`Stored rate: 1 ${r.currency} = ${(r.fx_rate || 1).toFixed(4)} ${currency}`}>
                                                            {r.currency} · {(r.fx_rate || 1).toFixed(2)}
                                                        </span>
                                                    )}
                                                    <span>{fmtMoney(r.amount, r.currency || currency)}</span>
                                                </div>
                                            </td>
                                            <td className="px-5 py-3 text-right whitespace-nowrap">
                                                <Button variant="ghost" size="icon" onClick={() => openEdit(r)} data-testid={`edit-expense-${r.id}`} title="Edit"><Pencil className="h-4 w-4 text-muted-foreground" /></Button>
                                                <Button variant="ghost" size="icon" onClick={() => duplicate(r.id)} data-testid={`duplicate-expense-${r.id}`} title="Duplicate"><Copy className="h-4 w-4 text-muted-foreground" /></Button>
                                                <Button variant="ghost" size="icon" onClick={() => remove(r.id)} title="Delete"><Trash2 className="h-4 w-4 text-muted-foreground" /></Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}

            <Dialog open={scanOpen} onOpenChange={(v) => { setScanOpen(v); if (!v) { setScanFile(null); } }}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-2xl flex items-center gap-2">
                            <Sparkles className="h-5 w-5 text-secondary" /> Scan receipt with AI
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <p className="text-sm text-muted-foreground">
                            Upload a receipt (PDF or image). BlueLedger will read it with GPT-4o Vision,
                            match the merchant (or create a new vendor), and pre-fill an expense draft
                            for your review.
                        </p>
                        <FileUpload
                            value={scanFile}
                            onChange={setScanFile}
                            testId="scan-expense-upload"
                            label="Upload receipt (PDF or image, up to 5 MB)"
                        />
                        <div className="rounded-2xl bg-sand-100 p-3 text-xs text-muted-foreground">
                            Tip: clear scans work best. The file will stay attached to the expense for audit.
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setScanOpen(false)} disabled={scanning}>Cancel</Button>
                        <Button
                            onClick={runScan}
                            disabled={!scanFile?.id || scanning}
                            className="bg-ocean-700 text-white"
                            data-testid="scan-expense-submit"
                        >
                            {scanning ? (<><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Scanning…</>) : (<><Sparkles className="h-4 w-4 mr-1.5" /> Scan &amp; open draft</>)}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
